import { apiListApps } from 'api/apps';
import { useQuery } from 'react-query';
import { queryKeys } from 'utils/react-query';
import { components, ControlProps, GroupBase, GroupHeadingProps, OptionProps, ValueContainerProps } from 'react-select';
import { Select } from 'components/SelectV2';
import TruncatedText from 'components/TruncatedText';
import { IconFilePreview } from 'components/Icons';
import { Checkbox } from 'components/Checkbox';
import { cx } from '@linaria/core';
import React, { useEffect, useMemo, useState } from 'react';
import { OptionType } from 'modules/Organizations/components/Search/types';
import { flex } from 'styles/utils';
import { groupStyles, selectWrapper } from './TeamsAndLogsDashboard.styles';

type Props<T extends Record<string, unknown>> = {
  userOrOrgId: string;
  selectedApps: OptionType<T>[];
  onAppChange: (apps: OptionType<T>[]) => void;
};

export const ALL_APPS_OPTION = { label: 'All Apps', value: 'all', role: { label: 'All Apps', value: 'all' }, rawValue: {} };

const Control = ({ children, ...props }: ControlProps<unknown, boolean, GroupBase<unknown>>) => {
  return (
    <components.Control {...props}>
      <IconFilePreview size={16} color="#344054" />
      {children}
    </components.Control>
  );
};

const ValueContainer = ({ children, getValue, ...props }: ValueContainerProps<unknown, boolean, GroupBase<unknown>>) => {
  const selectedValueLength = getValue().length;
  const { inputValue } = props.selectProps;
  const optionsLength = (props.selectProps.options as Array<{ label: string; options: [] }>)?.[0].options?.length;

  // Check if values are equal to options and display `All` instead of number.
  const displayLabelCount = optionsLength === selectedValueLength ? 'All' : selectedValueLength;

  return (
    <components.ValueContainer {...props} getValue={getValue}>
      {selectedValueLength > 0 && !inputValue && `${displayLabelCount} App${selectedValueLength !== 1 ? 's' : ''} selected`}
      {React.Children.map(children, (child) => (React.isValidElement(child) && child.type === components.Placeholder ? child : null))}
      {React.Children.map(children, (child) => {
        return React.isValidElement(child) && child.type === components.Input ? child : null;
      })}
    </components.ValueContainer>
  );
};

const GroupHeading = (props: GroupHeadingProps) => {
  const { data, selectProps } = props;
  const { value, onChange } = selectProps as {
    value: OptionType<unknown>[];
    onChange: (value: OptionType<unknown>[], actionMeta: { action: string; option?: OptionType<unknown> }) => void;
  };

  const groupOptions = data?.options as OptionType<unknown>[];

  // Check if all options in this group are selected
  const isAllSelected = groupOptions.every((option) => value.some((selected: OptionType<unknown>) => selected.value === option.value));

  const handleSelectAll = () => {
    if (isAllSelected) {
      onChange(
        value.filter((option) => !groupOptions.some((g) => g.value === option.value)),
        { action: 'deselect-option', option: undefined },
      );
    } else {
      onChange([...value, ...groupOptions.filter((o) => !value.some((v) => v.value === o.value))], { action: 'select-option' });
    }
  };

  return (
    <div className={groupStyles}>
      <components.GroupHeading {...props}>
        <div className={cx(flex.init, flex.justifyContent.between, flex.alignItems.center)}>
          {props.children}
          <Checkbox checked={isAllSelected} checkboxClassName="checkbox" onChange={handleSelectAll} />
        </div>
      </components.GroupHeading>
    </div>
  );
};

const Option = (props: JSX.IntrinsicAttributes & OptionProps<unknown, boolean, GroupBase<unknown>>) => {
  return (
    <components.Option {...props}>
      <div className="itemContainer" data-testid={`option-${props.label}`}>
        <div className="leftItems">
          <IconFilePreview size={16} color="#344054" />
          <TruncatedText customContainerWidth={400} text={props.label} className={props.isSelected ? 'active' : 'option-label'} />
        </div>
        <Checkbox checked={props.isSelected} checkboxClassName="checkbox" />
      </div>
    </components.Option>
  );
};

const getOptionMapper = <T extends Record<string, unknown>>(apps: CF.API.Apps.App[]): OptionType<T>[] => {
  return (apps?.map((app) => ({ label: app.name, value: app.id, rawValue: app })) ?? []) as OptionType<T>[];
};

export const AllAppsDropdown = <T extends Record<string, unknown>>({
  userOrOrgId,
  selectedApps: preSelectedApps,
  onAppChange,
}: Props<T>): JSX.Element => {
  const [selectedApps, setSelectedApps] = useState(preSelectedApps);
  const { data: { apps } = { apps: [] }, isLoading } = useQuery(
    [queryKeys.ListApps, userOrOrgId],
    () => apiListApps({ userId: userOrOrgId, params: `per_page=500&sort_by_name=true&sort_ascending=true` }),
    {
      enabled: Boolean(userOrOrgId),
    },
  );

  const options = useMemo(() => {
    return [
      {
        label: 'Select All Apps',
        options: getOptionMapper<T>(apps),
      },
    ];
  }, [apps]);

  useEffect(() => {
    setSelectedApps(getOptionMapper<T>(apps));
  }, [apps]);

  const onChange = (_apps: OptionType<T>[]) => {
    setSelectedApps(_apps);
    if (_apps.length === apps.length) {
      onAppChange([ALL_APPS_OPTION as OptionType<T>]);
    } else {
      onAppChange(_apps);
    }
  };

  return (
    <>
      <Select
        isSearchable
        isMulti
        className={cx(selectWrapper)}
        classNamePrefix="react-select"
        data-testid="apps-dropdown"
        placeholder="No App selected"
        onChange={(app) => onChange(app as OptionType<T>[])}
        value={selectedApps}
        options={options}
        components={{ Option, GroupHeading, Control, ValueContainer }}
        isLoading={isLoading}
        hideSelectedOptions={false}
      />
    </>
  );
};
